import React, { useEffect, useState } from "react";
import CourseViewer from "./CourseViewer";
import { PlayCircle, CheckCircle, Clock, BarChart, Award, Printer, X } from "lucide-react";
import semnaturaMuresanu from '../assets/semnatura-muresanu.png';
import stampilaImg from '../assets/stampila.png';

export default function CursuriSection({ memberId, memberName = "Nume Prenume" }) {
  const [cursuri, setCursuri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progresuri, setProgresuri] = useState({});
  const [cursCurent, setCursCurent] = useState(null);
  const [diplomaToShow, setDiplomaToShow] = useState(null);

  useEffect(() => {
    async function fetchData() {
      if(!memberId) return;
      try {
        setLoading(true);
        const [resCursuri, resProgresuri] = await Promise.all([
          fetch("https://prdp-api.suport-949.workers.dev/api/cursuri", {
            headers: { Authorization: "Bearer unwantedtok" },
          }),
          fetch(`https://prdp-api.suport-949.workers.dev/api/progres-membru/${memberId}`, {
            headers: { Authorization: "Bearer unwantedtok" },
          }),
        ]);

        if (!resCursuri.ok) throw new Error("Eroare încărcare cursuri");
        if (!resProgresuri.ok) throw new Error("Eroare încărcare progres cursuri");

        const cursuriData = await resCursuri.json();
        const progresData = await resProgresuri.json();

        setCursuri(cursuriData);

        const progresMap = {};
        progresData.forEach((p) => {
          progresMap[p.curs_id] = p;
        });
        setProgresuri(progresMap);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [memberId]);

  if (loading) return <div style={{ color: "#94a3b8", textAlign: "center", padding: "40px" }}>Se încarcă cursurile...</div>;
  if (error) return <div style={{ color: "#ef4444", textAlign: "center", padding: "40px" }}>{error}</div>;

  if (cursCurent) {
    return <CourseViewer courseId={cursCurent} memberId={memberId} onExit={() => setCursCurent(null)} />;
  }

  // ==========================================
  // VEDEREA PENTRU DIPLOMĂ (Anti-Tăiere la Print)
  // ==========================================
  if (diplomaToShow) {
    return (
      <div style={{ position: "fixed", inset: 0, backgroundColor: "#0b1120", zIndex: 9999, overflowY: "auto", padding: "40px 20px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        
        <style>
          {`
            @media print {
              @page { size: A4 landscape; margin: 0; }
              html, body { margin: 0 !important; padding: 0 !important; height: 100%; }
              body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: white; }
              body * { visibility: hidden; }
              #diploma-print-zone, #diploma-print-zone * { visibility: visible; }
              
              #diploma-print-zone { 
                position: absolute !important; 
                left: 0 !important; top: 0 !important; 
                
                /* Trucul de siguranță: 98% previne tăierea marginilor de către browser */
                width: 99vw !important; 
                height: 98vh !important; 
                
                max-width: none !important;
                margin: 1vh 0.5vw !important; /* O centrează milimetric în pagină */
                box-shadow: none !important;
              }
              .no-print { display: none !important; }
            }
          `}
        </style>

        {/* Buton Închidere */}
        <button onClick={() => setDiplomaToShow(null)} className="no-print" style={{ position: "absolute", top: "20px", right: "20px", background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", zIndex: 10001 }}>
          <X size={32} />
        </button>

        {/* CONTAINERUL CARE VA FI PRINTAT */}
        <div id="diploma-print-zone" style={{
            width: "100%", 
            maxWidth: "1000px", 
            aspectRatio: "297 / 210", 
            backgroundColor: "#fdfbf7", 
            border: "8px solid #002060",
            padding: "6px", 
            boxSizing: "border-box", 
            margin: "0 auto",
            position: "relative", 
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)", 
            fontFamily: "'Times New Roman', Times, serif",
            overflow: "hidden" /* Previne spargerea layout-ului dacă textul e prea lung */
        }}>
          
          {/* Chenar Auriu */}
          <div style={{ border: "2px solid #cfa840", padding: "4px", height: "100%", boxSizing: "border-box" }}>
            
            {/* Chenar Albastru Interior */}
            <div style={{
              border: "1px solid #002060", 
              padding: "4% 5%", 
              position: "relative", 
              height: "100%", 
              boxSizing: "border-box",
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center",
              justifyContent: "center"
            }}>
              
              {/* Header */}
              <div style={{ marginBottom: "2%" }}>
                <img src="/logo.webp" alt="PRDP Logo" style={{ height: "90px", objectFit: "contain" }} />
              </div>

              <h1 style={{ color: "#002060", fontSize: "clamp(1.5rem, 4vw, 2.6rem)", fontWeight: "bold", letterSpacing: "2px", margin: "1% 0", textTransform: "uppercase" }}>
                CERTIFICAT DE ABSOLVIRE
              </h1>
              
              <div style={{ width: "200px", height: "2px", backgroundColor: "#cfa840", margin: "2% auto" }}></div>
              
              {/* Corp */}
              <p style={{ color: "#333", fontSize: "clamp(1rem, 1.5vw, 1.2rem)", margin: "0" }}>Prezentul document atestă faptul că</p>
              
              <h2 style={{ color: "#002060", fontSize: "clamp(2rem, 5vw, 3.2rem)", margin: "1.5% 0", fontStyle: "italic", fontWeight: "bold" }}>
                {memberName}
              </h2>
              
              <p style={{ color: "#333", fontSize: "clamp(1rem, 1.5vw, 1.2rem)", margin: "0 0 2% 0" }}>
                a absolvit cu succes cursul de pregătire politică, obținând competențe în:
              </p>
              
              <h3 style={{ color: "#cfa840", fontSize: "clamp(1.4rem, 2.5vw, 2rem)", margin: "0", letterSpacing: "1px", padding: "0 5%" }}>
                {diplomaToShow.titlu}
              </h3>
              
              <p style={{ color: "#002060", fontSize: "clamp(0.9rem, 1.2vw, 1.1rem)", fontWeight: "bold", margin: "3% 0 0 0" }}>
                Punctaj obținut: 20 din 20 puncte
              </p>
              
              {/* SUBSOL DIPLOMĂ - ANCORAT ABSOLUT LA BAZA CHENARULUI */}
              <div style={{ 
                position: "absolute", 
                bottom: "5%", 
                left: "8%", 
                right: "8%", 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-end" 
              }}>
                
                {/* Data (Stânga) */}
                <div style={{ textAlign: "left", paddingBottom: "10px" }}>
                  <p style={{ color: "#333", fontSize: "clamp(0.9rem, 1.2vw, 1.1rem)", margin: "0" }}>
                    Data: {new Date().toLocaleDateString('ro-RO')}
                  </p>
                </div>
                
                {/* Blocul Semnăturii & Ștampilei (Dreapta) */}
                <div style={{ position: "relative", width: "250px", textAlign: "center" }}>
                  
                  <p style={{ color: "#000", fontSize: "clamp(0.8rem, 1vw, 0.95rem)", fontWeight: "bold", margin: "0 0 5px 0" }}>PREȘEDINTE PRDP</p>
                  <p style={{ color: "#000", fontSize: "clamp(0.9rem, 1.1vw, 1rem)", fontWeight: "bold", margin: "0 0 10px 0" }}>Alin Claudiu Mureșanu</p>
                  
                  {/* Semnătura */}
                  <div style={{ height: "60px", display: "flex", justifyContent: "center", alignItems: "center", position: "relative", zIndex: 1 }}>
                    <img src={semnaturaMuresanu} alt="Semnatura" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                  </div>

                  {/* Ștampila */}
                  <img 
                    src={stampilaImg}
                    alt="Stampila" 
                    style={{ 
                      position: "absolute",
                      width: "90px", 
                      height: "90px", 
                      right: "20px",  
                      bottom: "-15px",  
                      zIndex: 2,
                      pointerEvents: "none"
                    }} 
                  />

                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Butoane Acțiuni */}
        <div className="no-print" style={{ marginTop: "30px", display: "flex", gap: "20px" }}>
          <button onClick={() => window.print()} style={{ backgroundColor: "#002060", color: "#fff", border: "none", borderRadius: "8px", padding: "12px 24px", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
            <Printer size={20} /> Printează / Salvează PDF
          </button>
          <button onClick={() => setDiplomaToShow(null)} style={{ backgroundColor: "#cfa840", color: "#000", border: "none", borderRadius: "8px", padding: "12px 24px", fontWeight: "bold", cursor: "pointer" }}>
            Înapoi la cursuri
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // LISTA DE CURSURI STANDARD
  // ==========================================
  return (
    <>
      <style>{`
        .course-card-premium {
          background: #1e293b; border-radius: 20px; border: 1px solid rgba(255, 255, 255, 0.05);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3); padding: 24px; display: flex;
          flex-direction: column; justify-content: space-between; transition: transform 0.2s ease, box-shadow 0.2s ease;
          height: 100%;
        }
        .course-card-premium:hover { transform: translateY(-4px); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.4); border-color: rgba(255, 255, 255, 0.1); }
        .course-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px; border-radius: 12px;
          border: none; padding: 12px 0; font-weight: 700; font-size: 0.95rem; cursor: pointer;
          transition: all 0.2s ease; width: 100%; margin-top: 24px;
        }
        .course-btn-active { background: #fbbf24; color: #0f172a; }
        .course-btn-active:hover { background: #f59e0b; }
        .course-btn-done { background: rgba(16, 185, 129, 0.1); color: #10b981; cursor: default; }
      `}</style>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "24px", paddingBottom: "32px" }}>
        {cursuri.map((c) => {
          const prog = progresuri[c.id];
          const finalizat = prog ? prog.finalizat === true : false;
          const statusText = !prog ? "Începe cursul" : (finalizat ? "Curs finalizat" : "Continuă cursul");

          return (
            <div key={c.id} className="course-card-premium">
              <div>
                <h4 style={{ fontWeight: 700, fontSize: "1.2rem", color: "#f8fafc", marginBottom: "16px", lineHeight: "1.4" }}>
                  {c.titlu}
                </h4>
                
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", color: "#94a3b8", fontSize: "0.9rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <BarChart size={16} color="#fbbf24" />
                    <span>Dificultate: <strong>{c.dificultate}</strong></span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Clock size={16} color="#fbbf24" />
                    <span>Timp estimativ: <strong>{c.timp_estimativ}</strong></span>
                  </div>

                  {finalizat && (
                    <button 
                      onClick={() => setDiplomaToShow(c)}
                      style={{ 
                        display: "flex", alignItems: "center", gap: "6px", color: "#fbbf24", 
                        background: "none", border: "none", cursor: "pointer", 
                        padding: "8px 0 0 0", fontSize: "0.9rem", fontWeight: "600",
                        textDecoration: "underline", textUnderlineOffset: "4px"
                      }}
                    >
                      <Award size={16} /> Descarcă Diplomă
                    </button>
                  )}
                </div>
              </div>

              <button
                className={`course-btn ${finalizat ? "course-btn-done" : "course-btn-active"}`}
                disabled={finalizat}
                onClick={() => { if (!finalizat) setCursCurent(c.id); }}
              >
                {finalizat ? <CheckCircle size={18} /> : <PlayCircle size={18} />}
                {statusText}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
